'use no memo';

import {
  ActivityLog,
  Document,
  Event,
  getPropertyAddress,
  Invoice,
  Property,
  type User,
} from '@kalos/kalos-rpc';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  DataTable,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { useGetLoadingColumns } from '../../../components/utils';
import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME, ROWS_PER_PAGE } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { useEventUpdateMutation } from '../../../hooks/react-query/useEventsQuery';
import {
  type PropertyFilter,
  usePropertiesBatchGetQuery,
} from '../../../hooks/react-query/usePropertiesQuery';
import { useBatchUserQuery } from '../../../hooks/react-query/useUserQuery';
import {
  ActivityLogClientService,
  DocumentClientService,
  InvoiceClientService,
} from '../../../tools/helpers';

interface MoveJobToPropertyDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: number;
  jobNumber?: string;
  sourcePropertyId?: number;
  sourcePropertyAddress?: string;
}

interface PropertySearchFields {
  address: string;
  subdivision: string;
  city: string;
  zip: string;
}

const EMPTY_SEARCH: PropertySearchFields = { address: '', subdivision: '', city: '', zip: '' };

const loadingProperties: Property[] = Array.from({ length: 5 }, () => Property.create());

export const MoveJobToPropertyDialog = ({
  open,
  onClose,
  jobId,
  jobNumber,
  sourcePropertyId,
  sourcePropertyAddress,
}: MoveJobToPropertyDialogProps) => {
  const loggedUserId = useAuth().user.id;
  const queryClient = useQueryClient();
  const updateEventMutation = useEventUpdateMutation();

  const [searchFields, setSearchFields] = useState<PropertySearchFields>(EMPTY_SEARCH);
  const [searchFilter, setSearchFilter] = useState<PropertySearchFields>(EMPTY_SEARCH);
  const [page, setPage] = useState(0);
  const [targetProperty, setTargetProperty] = useState<Property | undefined>();
  const [moveDocumentsAndInvoices, setMoveDocumentsAndInvoices] = useState(false);
  const [moving, setMoving] = useState(false);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((fields: PropertySearchFields) => {
        setSearchFilter(fields);
        setPage(0);
      }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME),
    [],
  );
  useEffect(() => () => debouncedSetFilter.cancel(), [debouncedSetFilter]);

  const handleFieldChange =
    (field: keyof PropertySearchFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...searchFields, [field]: e.target.value };
      setSearchFields(next);
      debouncedSetFilter(next);
    };

  const handleReset = () => {
    debouncedSetFilter.cancel();
    setSearchFields(EMPTY_SEARCH);
    setSearchFilter(EMPTY_SEARCH);
    setPage(0);
  };

  const propertiesRequestFilter = useMemo<PropertyFilter>(() => {
    const request: PropertyFilter = {
      pageNumber: page,
      orderBy: 'property_address',
      orderDir: 'ASC',
      isActive: 1,
    };
    if (searchFilter.address) request.address = `%${searchFilter.address}%`;
    if (searchFilter.subdivision) request.subdivision = `%${searchFilter.subdivision}%`;
    if (searchFilter.city) request.city = `%${searchFilter.city}%`;
    if (searchFilter.zip) request.zip = `%${searchFilter.zip}%`;
    return request;
  }, [page, searchFilter]);

  const propertiesQuery = usePropertiesBatchGetQuery({
    filter: propertiesRequestFilter,
    enabled: open,
  });

  const ownerUserIds = useMemo(() => {
    if (!propertiesQuery.data?.results.length) return '';
    return [...new Set(propertiesQuery.data.results.map((p) => p.userId).filter(Boolean))].join(
      ',',
    );
  }, [propertiesQuery.data?.results]);

  const ownersQuery = useBatchUserQuery({
    filters: { userIdList: ownerUserIds, overrideLimit: true },
    enabled: ownerUserIds.length > 0,
  });

  const userMap = useMemo(() => {
    const map = new Map<number, User>();
    ownersQuery.data?.results?.forEach((user) => map.set(user.id, user));
    return map;
  }, [ownersQuery.data?.results]);

  const isLoading = propertiesQuery.isPending || (ownerUserIds.length > 0 && ownersQuery.isPending);

  const columns = useMemo<ColumnDef<Property>[]>(
    () => [
      {
        id: 'select',
        meta: { className: 'h-9 w-16' },
        cell: ({ row }) => (
          <Button disabled={row.original.id === sourcePropertyId}>
            {row.original.id === sourcePropertyId ? 'Current Property' : 'Select'}
          </Button>
        ),
      },
      {
        accessorKey: 'address',
        header: 'Address',
        meta: { className: 'h-4 w-32' },
        cell: ({ row }) => <div className="w-32">{row.original.address}</div>,
      },
      {
        id: 'customer',
        header: 'Customer',
        meta: { className: 'h-4 w-32' },
        cell: ({ row }) => {
          const user = userMap.get(row.original.userId);
          if (!user) return <div className="w-32">—</div>;
          const businessName = user.businessname?.trim();
          const fullName = [user.firstname?.trim(), user.lastname?.trim()]
            .filter(Boolean)
            .join(' ');
          return <div className="w-32">{businessName || fullName || '—'}</div>;
        },
      },
      {
        accessorKey: 'subdivision',
        header: 'Subdivision',
        meta: { className: 'h-4 w-32' },
        cell: ({ row }) => <div className="w-32">{row.original.subdivision}</div>,
      },
      {
        accessorKey: 'city',
        header: 'City',
        meta: { className: 'h-4 w-32' },
        cell: ({ row }) => <div className="w-32">{row.original.city}</div>,
      },
      {
        accessorKey: 'zip',
        header: 'Zip Code',
        meta: { className: 'h-4 w-32' },
        cell: ({ row }) => <div className="w-32">{row.original.zip}</div>,
      },
    ],
    [sourcePropertyId, userMap],
  );

  const loadingColumns = useGetLoadingColumns(columns);

  const table = useReactTable({
    data: isLoading ? loadingProperties : propertiesQuery.data?.results ?? [],
    columns: isLoading ? loadingColumns : columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalCount = propertiesQuery.data?.totalCount ?? 0;
  const pageCount = Math.ceil(totalCount / ROWS_PER_PAGE);

  const handleSelectProperty = (property: Property) => {
    if (isLoading || property.id === sourcePropertyId) return;
    setTargetProperty(property);
  };

  const handleCloseConfirm = () => {
    setTargetProperty(undefined);
    setMoveDocumentsAndInvoices(false);
    onClose();
  };

  const handleConfirmMove = async () => {
    if (!targetProperty) return;
    try {
      setMoving(true);

      const entry = Event.create();
      entry.id = jobId;
      entry.propertyId = targetProperty.id;
      entry.fieldMask = ['PropertyId'];
      await updateEventMutation.mutateAsync(entry);

      if (moveDocumentsAndInvoices) {
        const invoiceReq = Invoice.create();
        invoiceReq.eventId = jobId;
        const invoiceResults = await InvoiceClientService.BatchGet(invoiceReq);

        for (const invoice of invoiceResults?.results ?? []) {
          const invoiceUpdate = Invoice.create();
          invoiceUpdate.id = invoice.id;
          invoiceUpdate.propertyId = targetProperty.id;
          invoiceUpdate.userId = targetProperty.userId;
          invoiceUpdate.fieldMask = ['PropertyId', 'UserId'];
          await InvoiceClientService.Update(invoiceUpdate);

          const docReq = Document.create();
          docReq.invoiceId = invoice.id;
          const docResults = await DocumentClientService.BatchGet(docReq);

          for (const doc of docResults?.results ?? []) {
            const docUpdate = Document.create();
            docUpdate.id = doc.id;
            docUpdate.propertyId = targetProperty.id;
            docUpdate.userId = targetProperty.userId;
            docUpdate.fieldMask = ['PropertyId', 'UserId'];
            await DocumentClientService.Update(docUpdate);
          }
        }
      }

      const suffix = moveDocumentsAndInvoices ? ' (with documents/invoices)' : '';
      await ActivityLogClientService.Create(
        ActivityLog.create({
          propertyId: sourcePropertyId,
          userId: loggedUserId,
          activityName: `Job ${jobId} moved to ${targetProperty.address}${suffix}`,
        }),
      );
      await ActivityLogClientService.Create(
        ActivityLog.create({
          propertyId: targetProperty.id,
          userId: loggedUserId,
          activityName: `Job ${jobId} moved from ${sourcePropertyAddress ?? ''}${suffix}`,
        }),
      );

      await queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] });

      toast({
        title: 'Success',
        description: moveDocumentsAndInvoices
          ? 'Job moved successfully with all related documents and invoices'
          : 'Job moved successfully and activity logs created',
        variant: 'success',
      });

      setMoving(false);
      handleCloseConfirm();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to move job. Please try again.',
        variant: 'destructive',
      });
      console.error('Error moving job:', error);
      setMoving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open && !targetProperty}
        onOpenChange={(isOpen) => {
          if (!isOpen) onClose();
        }}
      >
        <DialogContent className="flex max-h-[90vh] max-w-6xl flex-col bg-white">
          <DialogHeader>
            <DialogTitle>Select Property To Move</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-[repeat(4,minmax(0,1fr))_5rem]">
            <div className="space-y-1">
              <Label htmlFor="move-job-address">Address</Label>
              <Input
                id="move-job-address"
                value={searchFields.address}
                onChange={handleFieldChange('address')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="move-job-subdivision">Subdivision</Label>
              <Input
                id="move-job-subdivision"
                value={searchFields.subdivision}
                onChange={handleFieldChange('subdivision')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="move-job-city">City</Label>
              <Input
                id="move-job-city"
                value={searchFields.city}
                onChange={handleFieldChange('city')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="move-job-zip">Zip Code</Label>
              <Input
                id="move-job-zip"
                value={searchFields.zip}
                onChange={handleFieldChange('zip')}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-auto max-w-max"
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between px-2">
            <DataTablePagination pageCount={pageCount} currentPage={page} setPage={setPage} />
            <p className="text-sm">Total Entries count: {totalCount}</p>
          </div>
          <div className="max-w-full flex-1 overflow-auto [&_thead]:bg-white">
            <DataTable table={table} onRowClick={handleSelectProperty} />
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={open && !!targetProperty}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleCloseConfirm();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Move Job</AlertDialogTitle>
            <AlertDialogDescription>
              Move job #{jobNumber || jobId} to{' '}
              <strong>{targetProperty ? getPropertyAddress(targetProperty) : ''}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 py-2">
            <Checkbox
              id="move-docs-invoices"
              checked={moveDocumentsAndInvoices}
              onCheckedChange={(checked) => setMoveDocumentsAndInvoices(checked === true)}
            />
            <Label htmlFor="move-docs-invoices" className="cursor-pointer text-sm">
              Move all Documents/Invoices related to this job
            </Label>
          </div>
          <AlertDialogFooter>
            <Button variant="secondary" onClick={handleCloseConfirm}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMove} disabled={moving}>
              {moving ? 'Moving...' : 'Confirm Move'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
